import { useMemo, useState } from 'react';

import { Song } from '~/interfaces';
import { Icon } from '~/modules/elements/akui/icon';
import { RegisterFunc } from '~/modules/hooks/use-keyboard-nav';
import SongQueue from '~/modules/song-queue/song-queue';
import useSongIndex from '~/modules/songs/hooks/use-song-index';
import { findSimilarSongs } from '~/modules/songs/utils/song-similarity';

interface Props {
  song: Song;
  register: RegisterFunc;
}

/**
 * "More like that" - offered here because this is the last screen before the song list, where the
 * singers decide what happens next.
 *
 * Picking one queues it rather than starting it: the running order already takes over when a song
 * ends, so a suggestion accepted here plays next without anybody going back to the list. It also
 * means several can be lined up at once, and that whoever is mid-conversation is not cut off by the
 * game jumping straight into another song.
 */
export default function SimilarSongs({ song, register }: Props) {
  const { data: songIndex } = useSongIndex();
  const [queued, setQueued] = useState<string[]>([]);

  const similar = useMemo(() => findSimilarSongs(song, songIndex), [song, songIndex]);

  if (similar.length === 0) return null;

  return (
    <div className="mt-2 flex flex-col gap-1" data-test="similar-songs">
      <span className="typography text-xs text-white/50 2xl:text-sm">More like this</span>
      <div className="flex flex-col gap-1 sm:flex-row">
        {similar.map(({ song: suggestion, reason }) => {
          const isQueued = queued.includes(suggestion.id);

          return (
            <button
              key={suggestion.id}
              {...register(
                `similar-song-${suggestion.id}`,
                () => {
                  if (SongQueue.add(suggestion)) setQueued((current) => [...current, suggestion.id]);
                },
                undefined,
                false,
                {
                  disabled: isQueued,
                  control: { type: 'button', label: `Queue ${suggestion.artist}` },
                },
              )}
              data-test={`similar-song-${suggestion.id}`}
              data-queued={isQueued}
              className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-white/10 bg-black/40 px-2 py-1 text-left focus:border-white/60 disabled:opacity-60">
              <Icon
                icon={isQueued ? 'ic:baseline-check' : 'ic:baseline-add'}
                className={isQueued ? 'shrink-0 text-green-400' : 'shrink-0 text-white/60'}
              />
              <span className="min-w-0 flex-1">
                <span className="ph-no-capture typography block truncate text-sm">{suggestion.title}</span>
                <span className="ph-no-capture block truncate text-xs text-white/40">
                  {suggestion.artist} · {isQueued ? 'queued' : reason}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
